import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import VacationsService from '@services/VacationsService';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import * as XLSX from 'xlsx';
import './PTOReport.css';
import moment from 'moment';
import { countWorkingDays } from '@lib/date';
import { ClearIndicatorComponent as ClearIndicator } from '@/components/Reports/ClearIndicatorComponent';
import { buildReportSelectStyles } from '@/components/Reports/reportSelectStyles';

interface AppConfig {
  requestId?: string | number;
  [key: string]: unknown;
}

export interface PTOReportProps {
  config: AppConfig;
}

interface Person {
  PersonID: number;
  Name: string;
  Team?: string;
  TeamId?: number;
  DepartmentId?: number;
}

interface TeamItem {
  BasicFieldValueId: number;
  Value: string;
}

interface DepartmentItem {
  BasicFieldValueId: number;
  Value: string;
}

interface ProjectItem {
  ProjectID: number;
  Name: string;
}

interface RequestType {
  value: string;
  label: string;
}

interface StatusOption {
  value: number;
  label: string;
}

interface Holiday {
  Date: string;
}

interface Permission {
  Id: number;
  Days: number;
}

interface TableRow {
  id: number;
  creationDate: string;
  team: string;
  requestedBy: string;
  employeeID: number;
  leadName: string;
  requestTypeName: string;
  days: number;
  dateFrom: string;
  dateTo: string;
  workedOn: string;
  daysLeft: string | number;
  startPeriod: string;
  entryDate: string;
  requesterComments: string;
  approverComments: string;
  status: string;
  projectNames: string;
  customFields: Record<string, string>;
  requestId: number;
  enabled: boolean;
  isFirstRendered: boolean;
}

type SortDirection = 'asc' | 'desc' | '';

function strDateToMMDDYYYY(str: string): string
{
  const [y, m, d] = str.split('-') as [string, string, string];
  return `${m}/${d}/${y}`;
}

function parseDate(str: string): Date
{
  const [y, m, d] = str.split('-').map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
}

/**
 * Normalizes an API date to local midnight. `GetHireDate` returns `MM/DD/YYYY` while other
 * endpoints return `YYYY-MM-DD[THH:mm:ss]`, so both are accepted. Parsing explicitly avoids
 * `new Date(str)`, which reads date-only ISO strings as UTC and shifts the day in negative
 * offsets, landing day arithmetic on the wrong side of the anniversary.
 */
function parseApiDate(str: string): Date
{
  const datePart = str.split('T')[0]!.trim();
  return datePart.includes('/') ? mmddyyyyToDate(datePart) : parseDate(datePart);
}

/** Formats a local Date as `YYYY-MM-DD` without the UTC shift of `toISOString()`. */
function toLocalISODate(date: Date): string
{
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${m}-${d}`;
}

/**
 * Completed years of service, counted by calendar anniversary.
 * Dividing a day count by 365 drifts with leap years and cannot land exactly on the
 * renewal date, so the anniversary is compared directly instead.
 */
function calcYearsOfService(hireDate: string, reference: Date): number
{
  const hire = parseApiDate(hireDate);
  // An unparseable hire date must not fall through the reform tables as NaN, which would
  // skip every threshold and report the maximum allowance.
  if (Number.isNaN(hire.getTime())) return 0;

  const ref = new Date(reference.getFullYear(), reference.getMonth(), reference.getDate());
  const anniversary = new Date(ref.getFullYear(), hire.getMonth(), hire.getDate());
  let years = ref.getFullYear() - hire.getFullYear();
  if (ref < anniversary) years--;
  return Math.max(0, years);
}

function mmddyyyyToDate(str: string): Date
{
  const [m, d, y] = str.split('/').map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
}

function calcDaysBetweenDates(start: number, end: number): number
{
  return Math.round((end - start) / (1000 * 60 * 60 * 24));
}

function checkEnabledDate(startDate: Date): boolean
{
  const today = new Date().toISOString().split('T')[0]!;
  return startDate.getTime() > parseDate(today).getTime();
}

function getRequestStatus(e: number): string
{
  if (e === 0) return 'Requested';
  if (e === 1) return 'Approved';
  return 'Rejected';
}

function getVacationDaysNewReform(years: number): number
{
  if (years < 1) return 0;
  if (years < 6) return 10 + 2 * years;
  if (years < 36) return 20 + 2 * Math.floor(years * 0.195);
  return 32;
}

function getVacationDaysOldReform(years: number): number
{
  if (years < 1) return 6;
  if (years < 6) return 4 + 2 * years;
  if (years < 34) return 12 + 2 * Math.floor(years * 0.2);
  return 24;
}

function getVacationDaysForYears(hiredDate: string, yearsHired: number): number
{
  const hiredAfter2023 = parseApiDate(hiredDate) >= new Date(2023, 0, 1);
  const isRenewed = parseApiDate(hiredDate).setFullYear(2023) <= Date.now();
  const currentYear = new Date().getFullYear();
  return hiredAfter2023 || isRenewed || currentYear > 2023
    ? getVacationDaysNewReform(yearsHired)
    : getVacationDaysOldReform(yearsHired);
}

/** Vacation days for the previous (0), current (1) or next (2) vacation period. */
function getVacationDaysForPeriodOffset(
  hiredDate: string,
  yearsHired: number,
  offset: 0 | 1 | 2,
): number
{
  const years = offset === 0 ? yearsHired - 1 : offset === 2 ? yearsHired + 1 : yearsHired;
  return getVacationDaysForYears(hiredDate, Math.max(0, years));
}

function cleanText(html: string): string
{
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.innerText;
}

function getCustomFields(results: Record<string, unknown>): Record<string, string>
{
  return Object.fromEntries(
    Object.entries(results)
      .filter(([k]) => k.includes('cf_'))
      .map(([k, v]) => [k.replace('_', ' '), cleanText(String(v ?? ''))]),
  );
}

function getTotalCustomFields(rows: Record<string, string>[]): Record<string, number>
{
  return rows.reduce<Record<string, number>>((acc, row) =>
  {
    for (const [k, v] of Object.entries(row))
    {
      acc[k] = (acc[k] ?? 0) + parseFloat(v);
    }
    return acc;
  }, {});
}

function getCustomFieldHeader(key: string): string
{
  const suffix = key.replaceAll('cf', '').replaceAll('_', ' ');
  return suffix.charAt(0).toUpperCase() + suffix.slice(1);
}

function sortByMMDDYYYY(a: string | undefined, b: string | undefined, dir: SortDirection): number
{
  if (!a) return 1;
  if (!b) return -1;
  const flip = dir === 'asc' ? 1 : -1;
  return flip * (mmddyyyyToDate(a).getTime() - mmddyyyyToDate(b).getTime());
}

// Date input component for react-datepicker
interface DateInputProps {
  value?: string;
  onClick?: () => void;
  onClear: () => void;
}
const PtoDateInput = React.forwardRef<HTMLInputElement, DateInputProps>(
  ({ value, onClick, onClear }, ref) => (
    <div className="er-date-input-wrapper">
      <input
        ref={ref}
        type="text"
        value={value || ''}
        onClick={onClick}
        readOnly
        className="er-date-input"
        placeholder="MM/DD/YYYY"
        onChange={() => {}}
      />
      <button
        type="button"
        className="er-date-icon-btn"
        onClick={value ? onClear : onClick}
        tabIndex={-1}
      >
        <i className="fa fa-calendar" style={{ fontSize: 12 }} />
      </button>
    </div>
  ),
);
PtoDateInput.displayName = 'PtoDateInput';

const SortIcon: React.FC<{ col: string; sorted: string; dir: SortDirection }> = ({
  col,
  sorted,
  dir,
}) => (
  <i
    className={
      sorted === col ? (dir === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc') : 'fa fa-sort'
    }
    style={{ display: 'inline-block', width: 20 }}
  />
);

const SortTh: React.FC<{
  col: string;
  label: string;
  sorted: string;
  dir: SortDirection;
  onSort: (c: string) => void;
  tooltip?: string;
}> = ({ col, label, sorted, dir, onSort, tooltip }) => (
  <th className="enable-help-tooltip" data-title={tooltip ?? label} onClick={() => onSort(col)}>
    <span className="headerIconPadding">{label}</span>
    <SortIcon col={col} sorted={sorted} dir={dir} />
  </th>
);

const PTOReport: React.FC<PTOReportProps> = ({ config }) =>
{
  const [peopleAvailable, setPeopleAvailable] = useState<Person[]>([]);
  const [itemsTeams, setItemsTeams] = useState<TeamItem[]>([]);
  const [itemsDepartments, setItemsDepartments] = useState<DepartmentItem[]>([]);
  const [itemsProjects, setItemsProjects] = useState<ProjectItem[]>([]);
  const [itemsPermissions, setItemsPermissions] = useState<Permission[]>([]);
  const [requestTypes, setRequestTypes] = useState<RequestType[]>([]);
  const [statusList, setStatusList] = useState<StatusOption[]>([]);
  // Latest holidays for async chains to avoid stale-closure reads
  const itemsHolidaysRef = useRef<Holiday[]>([]);

  const [selectTeamsId, setSelectTeamsId] = useState<TeamItem[]>([]);
  const [selectDepartmentsId, setSelectDepartmentsId] = useState<DepartmentItem[]>([]);
  const [selectLeadId, setSelectLeadId] = useState<Person[]>([]);
  const [selectRequestedIds, setSelectRequestedIds] = useState<Person[]>([]);
  const [selectRequestType, setSelectRequestType] = useState<RequestType[]>([]);
  const [selectStatusId, setSelectStatusId] = useState<StatusOption[]>([]);
  const [selectProjectId, setSelectProjectId] = useState<ProjectItem[]>([]);

  const [selectedDateFrom, setSelectedDateFrom] = useState('');
  const [selectedDateTo, setSelectedDateTo] = useState('');
  const [selectedDateFromEmpty, setSelectedDateFromEmpty] = useState(false);
  const [selectedDateToEmpty, setSelectedDateToEmpty] = useState(false);
  const [selectedDateFromLess, setSelectedDateFromLess] = useState(false);

  const [isLoadingRequests, setIsLoadingRequests] = useState(true);
  const [showOnlyFirstVacation, setShowOnlyFirstVacation] = useState(false);
  const [validationFilter, setValidationFilter] = useState(false);
  const [filterApplied, setFilterApplied] = useState(false);

  const [itemsFillTable, setItemsFillTable] = useState<TableRow[]>([]);
  const [prevItemsFillTable, setPrevItemsFillTable] = useState<TableRow[]>([]);
  const [originalItems, setOriginalItems] = useState<TableRow[]>([]);
  const [dataTableTotalCustomField, setDataTableTotalCustomField] = useState<
    Record<string, number>
  >({});

  const [tableSortedBy, setTableSortedBy] = useState('default');
  const [sortingDirection, setSortingDirection] = useState<SortDirection>('');

  // PTO calculations
  const [hireDate, setHireDate] = useState<string | null>(null);
  const [yearsOfService, setYearsOfService] = useState<number | null>(null);
  const [renewalDate, setRenewalDate] = useState<string | null>(null);
  const [nextRenewalDate, setNextRenewalDate] = useState<string | null>(null);
  const [vacationsPerYear, setVacationsPerYear] = useState(0);
  const [vacationsLeft, setVacationsLeft] = useState(0);
  const [switchesAvailable] = useState(6);
  const [dayOffAvailable, setDayOffAvailable] = useState(5);
  const [dayOffLeft, setDayOffLeft] = useState(0);
  const [totalSwitchesDays, setTotalSwitchesDays] = useState(0);
  const [totalSwitchesOff, setTotalSwitchesOff] = useState(0);
  const [vacationsLeftNextPeriod, setVacationsLeftNextPeriod] = useState(0);
  const [vacationsPerYearNextPeriod, setVacationsPerYearNextPeriod] = useState(0);

  const firstRenderedRequestRef = useRef<number>(parseInt(String(config.requestId ?? '0')) || 0);
  const today = new Date().toISOString().split('T')[0]!;

  // Date range notification
  const [showDateRangeMsg, setShowDateRangeMsg] = useState(false);
  const dateRangeMsgTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() =>
  {
    document.title = 'Corgee';
  }, []);

  useEffect(() =>
  {
    // Load holidays before the first query so working-day requests deduct them
    VacationsService.GetHolidays()
      .then((d: Holiday[]) =>
      {
        itemsHolidaysRef.current = d;
        return VacationsService.GetAllAsignedTo({
          Teams: '',
          GetTeamId: true,
          GetDepartmentId: true,
          GetAllPeople: true,
          GetAllInfo: true,
        }) as Promise<any>;
      })
      .then((data: Person[]) =>
      {
        setPeopleAvailable(data);
        getCurrentWeekRequests(data);
      });

    VacationsService.GetAllTeams().then((d: TeamItem[]) => setItemsTeams(d));
    VacationsService.GetAllDepartments().then((d: DepartmentItem[]) => setItemsDepartments(d));
    VacationsService.GetAllProject({
      GroupByDetailId: 0,
      GroupByLayerId: 0,
      GroupByValueField: '',
    }).then((d: { Projects: ProjectItem[] }) => setItemsProjects(d.Projects));
    VacationsService.GetPTOPermissions().then((d: Permission[]) => setItemsPermissions(d));
    VacationsService.GetPTOFilterOptions().then((d: { RequestTypeNames: string[] }) =>
    {
      setRequestTypes(d.RequestTypeNames.map(t => ({ value: t, label: t })));
      setStatusList([
        { value: 0, label: 'Requested' },
        { value: 1, label: 'Approved' },
        { value: 2, label: 'Rejected' },
        { value: 3, label: 'Cancelled' },
      ]);
    });
  }, []);

  const getCurrentWeekRequests = useCallback((people: Person[]) =>
  {
    const todayDate = new Date();
    const currentDay = todayDate.getDay();
    const start = new Date(todayDate);
    if (currentDay !== 1) start.setDate(todayDate.getDate() - ((currentDay + 6) % 7));
    const end = new Date(start);
    end.setDate(start.getDate() + 4);

    const from = moment(start).format('YYYY-MM-DD');
    const to = moment(end).format('YYYY-MM-DD');
    setSelectedDateFrom(from);
    setSelectedDateTo(to);

    // Run getResults immediately with known values
    getResultsWithParams({ people, from, to });
  }, []);

  const getResultsWithParams = useCallback(
    ({
      people = peopleAvailable,
      from = selectedDateFrom,
      to = selectedDateTo,
      teamsId = selectTeamsId,
      deptsId = selectDepartmentsId,
      reqIds = selectRequestedIds,
      leadIds = selectLeadId,
      reqType = selectRequestType,
      statIds = selectStatusId,
      projIds = selectProjectId,
      onlyFirst = showOnlyFirstVacation,
    }: {
      people?: Person[];
      from?: string;
      to?: string;
      teamsId?: TeamItem[];
      deptsId?: DepartmentItem[];
      reqIds?: Person[];
      leadIds?: Person[];
      reqType?: RequestType[];
      statIds?: StatusOption[];
      projIds?: ProjectItem[];
      onlyFirst?: boolean;
    } = {}) =>
    {
      if (to && from && to < from)
      {
        setSelectedDateFromLess(true);
        return;
      }
      if (from && moment(from).isBefore(moment().subtract(2, 'years')))
      {
        if (dateRangeMsgTimerRef.current) clearTimeout(dateRangeMsgTimerRef.current);
        setShowDateRangeMsg(true);
        dateRangeMsgTimerRef.current = setTimeout(() => setShowDateRangeMsg(false), 3000);
        return;
      }

      let filtered = [...people];
      if (teamsId.length)
        filtered = filtered.filter(p =>
          teamsId.map(x => x.BasicFieldValueId).includes(p.TeamId ?? -1),
        );
      if (deptsId.length)
        filtered = filtered.filter(p =>
          deptsId.map(x => x.BasicFieldValueId).includes(p.DepartmentId ?? -1),
        );

      const peopleIds = filtered.map(p => p.PersonID).join(',') || '0';
      const selectProject = projIds.map(x => x.ProjectID).join(',');
      const selectLead = leadIds.map(x => x.PersonID).join(',');
      const selectReqType = reqType.map(x => x.value).join(',');
      const selectStatus = statIds.map(x => x.value).join(',');
      const selectRequested = reqIds.map(x => x.PersonID).join(',');

      setFilterApplied(reqIds.length === 1);

      const allEmpty =
        !peopleIds &&
        !selectProject &&
        !selectLead &&
        !selectReqType &&
        !selectStatus &&
        !selectRequested &&
        !from &&
        !to &&
        !onlyFirst;

      if (allEmpty)
      {
        setValidationFilter(true);
        setItemsFillTable([]);
        return;
      }

      const filters = {
        RequestedBy: selectRequested,
        PeopleIds: peopleIds,
        ProjectIds: selectProject,
        Leads: selectLead,
        RequestTypeNames: selectReqType,
        Status: selectStatus,
        DateFrom: from ? from.split('T')[0] : null,
        DateTo: to ? to.split('T')[0] : null,
        SelectedRequestId: firstRenderedRequestRef.current,
        OnlyFirstVacation: onlyFirst,
      };

      fillTable(filters, people);
    },
    [
      peopleAvailable,
      selectedDateFrom,
      selectedDateTo,
      selectTeamsId,
      selectDepartmentsId,
      selectRequestedIds,
      selectLeadId,
      selectRequestType,
      selectStatusId,
      selectProjectId,
      showOnlyFirstVacation,
    ],
  );

  const getResults = useCallback(() =>
  {
    getResultsWithParams({});
  }, [getResultsWithParams]);

  const fillTable = useCallback(
    async (filters: Record<string, unknown>, people: Person[]) =>
    {
      setIsLoadingRequests(true);

      const data: Record<string, unknown>[] = await VacationsService.GetFilteredResults(filters);
      const rows: TableRow[] = [];
      const customsArr: Record<string, string>[] = [];

      for (let i = 0; i < data.length; i++)
      {
        const item = data[i]!;
        const dateFrom = new Date(String(item.DateFrom).split('T')[0]!);
        const dateTo = new Date(String(item.DateTo).split('T')[0]!);

        let days = calcDaysBetweenDates(dateFrom.getTime(), dateTo.getTime()) + 1;
        if (item.IsWorkingDay) days = countWorkingDays(dateFrom, dateTo, itemsHolidaysRef.current);

        const dateFromStr = strDateToMMDDYYYY(String(item.DateFrom).split('T')[0]!);
        const dateToStr = strDateToMMDDYYYY(String(item.DateTo).split('T')[0]!);
        const workedOn = item.WorkedOn
          ? strDateToMMDDYYYY(String(item.WorkedOn).split('T')[0]!)
          : '';
        const isComplete = workedOn !== dateFromStr;

        const requester = people.find(p => p.PersonID === Number(item.PersonId));
        const lead = people.find(p => p.PersonID === Number(item.LeadId));

        if (item.RequestTypeName === 'Day Off' && days === 0) days = 1;
        if (!isComplete || !requester) continue;

        const cf = getCustomFields(item as Record<string, unknown>);
        customsArr.push(cf);

        // Use DaysLeft as-is from backend (already formatted, e.g., "11 / 16")
        const daysLeftValue = String(item.DaysLeft ?? '');

        rows.push({
          id: i + 1,
          creationDate: strDateToMMDDYYYY(String(item.CreatedAt).split('T')[0]!),
          team: requester.Team ?? '',
          requestedBy: requester.Name ?? '',
          employeeID: requester.PersonID,
          leadName: lead?.Name ?? 'Admin',
          requestTypeName: String(item.RequestTypeName ?? ''),
          days,
          dateFrom: dateFromStr,
          dateTo: dateToStr,
          workedOn,
          daysLeft: daysLeftValue,
          startPeriod: strDateToMMDDYYYY(String(item.StartPeriod).split('T')[0]!),
          entryDate: strDateToMMDDYYYY(String(item.HireDate).split('T')[0]!),
          requesterComments: String(item.Comments ?? ''),
          approverComments: String(item.ApproverComments ?? ''),
          status: item.IsActive ? getRequestStatus(Number(item.Status)) : 'Cancelled',
          projectNames: String(item.ProjectNames ?? ''),
          customFields: cf,
          requestId: Number(item.VacationRequestId),
          enabled: item.IsActive ? checkEnabledDate(dateFrom) : false,
          isFirstRendered: Number(item.VacationRequestId) === firstRenderedRequestRef.current,
        });
      }

      // Bubble up the notification item
      if (firstRenderedRequestRef.current > 0)
      {
        const idx = rows.findIndex(r => r.isFirstRendered);
        if (idx > 0) rows.unshift(rows.splice(idx, 1)[0]!);
      }

      const totals = getTotalCustomFields(customsArr);
      firstRenderedRequestRef.current = 0;

      const sortedRows = [...rows].sort(
        (a, b) => mmddyyyyToDate(b.dateFrom).getTime() - mmddyyyyToDate(a.dateFrom).getTime(),
      );

      setItemsFillTable(sortedRows);
      setPrevItemsFillTable([...sortedRows]);
      setOriginalItems([...sortedRows]);
      setDataTableTotalCustomField(totals);
      setTableSortedBy('default');
      setSortingDirection('');
      setIsLoadingRequests(false);
    },
    [],
  );

  const calcPTODaysLeft = useCallback(
    (
      approvedItems: Array<{ requestTypeName: string; creationDate: string; days: number }>,
      renewal: string,
      vacPerYear: number,
      dayOffAvail: number,
      perms: Permission[],
      yearsHired: number,
      hireDateStr: string,
    ) =>
    {
      // Period boundaries are compared as local dates. `new Date('YYYY-MM-DD')` parses as
      // UTC midnight while `new Date('MM/DD/YYYY')` parses as local midnight, so mixing
      // them shifts the window by a day.
      const renewalStart = parseDate(renewal);

      const calcBeforePeriod = (): number =>
      {
        if (yearsHired > 1)
        {
          const renewalBeforeDate = new Date(
            renewalStart.getFullYear() - 1,
            renewalStart.getMonth(),
            renewalStart.getDate(),
          );

          const askedCountBefore = approvedItems
            .filter(
              x =>
                x.requestTypeName === 'Vacations' &&
                mmddyyyyToDate(x.creationDate) >= renewalBeforeDate &&
                mmddyyyyToDate(x.creationDate) < renewalStart,
            )
            .reduce((s, x) => s + x.days, 0);

          const vacPrevYear = getVacationDaysForPeriodOffset(hireDateStr, yearsHired, 0);
          if (askedCountBefore > vacPrevYear) return askedCountBefore - vacPrevYear;
        }
        return 0;
      };

      const askedBeforeVacations = calcBeforePeriod();

      const askedVacationsOnPeriod = approvedItems
        .filter(
          x => x.requestTypeName === 'Vacations' && mmddyyyyToDate(x.creationDate) >= renewalStart,
        )
        .reduce((s, x) => s + x.days, 0);

      const askedVacations = askedVacationsOnPeriod + askedBeforeVacations;

      const yearStart = new Date(new Date().getFullYear(), 0, 1);
      const askedSwitches = approvedItems.filter(
        x => x.requestTypeName === 'Switch' && new Date(x.creationDate) >= yearStart,
      ).length;
      const totalSwitchesDaysVal =
        approvedItems.filter(
          x => x.requestTypeName === 'Switch - Work Day' && new Date(x.creationDate) >= yearStart,
        ).length + askedSwitches;
      const totalSwitchesOffVal =
        approvedItems.filter(
          x => x.requestTypeName === 'Switch - Day Off' && new Date(x.creationDate) >= yearStart,
        ).length + askedSwitches;
      const askedDayOff = approvedItems.filter(
        x => x.requestTypeName === 'Day Off' && new Date(x.creationDate) >= yearStart,
      ).length;

      const perm = perms.find(p => p.Id === 6);
      const totalDaysOff = perm?.Days ?? 0;

      let vacationsLeftNextPeriodVal = 0;
      let vacationsPerYearNextPeriodVal = 0;

      if (vacPerYear - askedVacations < 1)
      {
        vacationsPerYearNextPeriodVal = getVacationDaysForPeriodOffset(hireDateStr, yearsHired, 2);
        vacationsLeftNextPeriodVal =
          vacationsPerYearNextPeriodVal -
          askedBeforeVacations -
          (askedVacationsOnPeriod - vacPerYear);
      }

      setVacationsLeft(vacPerYear - askedVacations < 1 ? 0 : vacPerYear - askedVacations);
      setTotalSwitchesDays(totalSwitchesDaysVal);
      setTotalSwitchesOff(totalSwitchesOffVal);
      setDayOffLeft(Math.max(0, dayOffAvail - askedDayOff));
      setDayOffAvailable(totalDaysOff);
      setVacationsLeftNextPeriod(vacationsLeftNextPeriodVal);
      setVacationsPerYearNextPeriod(vacationsPerYearNextPeriodVal);
    },
    [],
  );

  const getVacationRequestsForPTO = useCallback(
    (
      personId: number,
      renewal: string,
      vacPerYear: number,
      perms: Permission[],
      yearsHired: number,
      hireDateStr: string,
    ) =>
    {
      VacationsService.GetVacationRequestByIds({
        PersonId: personId,
        PersonIds: String(personId),
        DateFrom: today,
        DateTo: today,
        GetAll: true,
      }).then((data: Record<string, unknown>[]) =>
      {
        const items = data.map(item =>
        {
          const from = new Date(String(item.DateFrom).split('T')[0]!);
          const to = new Date(String(item.DateTo).split('T')[0]!);
          let days = calcDaysBetweenDates(from.getTime(), to.getTime()) + 1;
          if (item.IsWorkingDay) days = countWorkingDays(from, to, itemsHolidaysRef.current);
          return {
            requestTypeName: String(item.RequestTypeName),
            requestTypeId: Number(item.RequestTypeId),
            creationDate: strDateToMMDDYYYY(String(item.CreatedAt).split('T')[0]!),
            days,
            status: item.IsActive ? getRequestStatus(Number(item.Status)) : 'Cancelled',
          };
        });

        const active = items.filter(x => x.status === 'Approved' || x.status === 'Requested');
        calcPTODaysLeft(
          active,
          renewal,
          vacPerYear,
          dayOffAvailable,
          perms,
          yearsHired,
          hireDateStr,
        );
      });
    },
    [today, calcPTODaysLeft, switchesAvailable, dayOffAvailable],
  );

  const fetchHireDate = useCallback(
    (personId: number | null) =>
    {
      if (!personId)
      {
        setHireDate(null);
        setYearsOfService(null);
        setRenewalDate(null);
        setNextRenewalDate(null);
        setVacationsLeft(0);
        setVacationsPerYear(0);
        setDayOffLeft(0);
        return;
      }

      VacationsService.GetHireDate(personId).then((date: string | null) =>
      {
        if (!date)
        {
          setHireDate(null);
          setYearsOfService(null);
          setRenewalDate(null);
          setNextRenewalDate(null);
          setVacationsLeft(0);
          setVacationsPerYear(0);
          setDayOffLeft(0);
          return;
        }

        const now = new Date();
        const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const hireLocal = parseApiDate(date);
        const years = calcYearsOfService(date, todayLocal);

        // Renewal is the last anniversary already reached (the anniversary day itself counts).
        const renewal = new Date(
          hireLocal.getFullYear() + years,
          hireLocal.getMonth(),
          hireLocal.getDate(),
        );
        const nextRenewal = new Date(
          renewal.getFullYear() + 1,
          renewal.getMonth(),
          renewal.getDate(),
        );
        const renewalStr = toLocalISODate(renewal);

        const vacDays = getVacationDaysForYears(date, years);

        setHireDate(date);
        setYearsOfService(years);
        setRenewalDate(renewalStr);
        setNextRenewalDate(toLocalISODate(nextRenewal));
        setVacationsPerYear(vacDays);

        getVacationRequestsForPTO(personId, renewalStr, vacDays, itemsPermissions, years, date);
      });
    },
    [itemsPermissions, getVacationRequestsForPTO],
  );

  const sortTableBy = useCallback(
    (column: string) =>
    {
      if (!column || column === 'default')
      {
        setTableSortedBy('default');
        setSortingDirection('');
        setItemsFillTable([...originalItems]);
        return;
      }

      let nextDir: SortDirection;
      if (tableSortedBy === column && sortingDirection === 'asc') nextDir = 'desc';
      else if (tableSortedBy === column && sortingDirection === 'desc')
      {
        setTableSortedBy('default');
        setSortingDirection('');
        setItemsFillTable([...originalItems]);
        return;
      }
      else nextDir = 'asc';

      const base = [...prevItemsFillTable];
      const flip = nextDir === 'asc' ? 1 : -1;

      const strCmp = (a: string, b: string) => flip * a.localeCompare(b);
      const numCmp = (a: number, b: number) => flip * (a - b);

      let sorted: TableRow[];

      const resolvedCol = column.includes('cf') ? 'customfield' : column;

      switch (resolvedCol)
      {
        case 'creationDate':
          sorted = base.sort((a, b) => sortByMMDDYYYY(a.creationDate, b.creationDate, nextDir));
          break;
        case 'team':
          sorted = base.sort((a, b) => strCmp(a.team, b.team));
          break;
        case 'requestedBy':
          sorted = base.sort((a, b) => strCmp(a.requestedBy, b.requestedBy));
          break;
        case 'employeeID':
          sorted = base.sort((a, b) => numCmp(a.employeeID, b.employeeID));
          break;
        case 'lead':
          sorted = base.sort((a, b) => strCmp(a.leadName, b.leadName));
          break;
        case 'requestType':
          sorted = base.sort((a, b) => strCmp(a.requestTypeName, b.requestTypeName));
          break;
        case 'days':
          sorted = base.sort((a, b) => numCmp(a.days, b.days));
          break;
        case 'from':
          sorted = base.sort((a, b) => sortByMMDDYYYY(a.dateFrom, b.dateFrom, nextDir));
          break;
        case 'to':
          sorted = base.sort((a, b) => sortByMMDDYYYY(a.dateTo, b.dateTo, nextDir));
          break;
        case 'workedOn':
          sorted = base.sort((a, b) =>
            sortByMMDDYYYY(a.workedOn || undefined, b.workedOn || undefined, nextDir),
          );
          break;
        case 'daysLeft':
          sorted = base.sort((a, b) => strCmp(String(a.daysLeft), String(b.daysLeft)));
          break;
        case 'startPeriod':
          sorted = base.sort((a, b) =>
            sortByMMDDYYYY(a.startPeriod || undefined, b.startPeriod || undefined, nextDir),
          );
          break;
        case 'entryDate':
          sorted = base.sort((a, b) =>
            sortByMMDDYYYY(a.entryDate || undefined, b.entryDate || undefined, nextDir),
          );
          break;
        case 'requesterComments':
          sorted = base.sort((a, b) => strCmp(a.requesterComments, b.requesterComments));
          break;
        case 'approverComments':
          sorted = base.sort((a, b) => strCmp(a.approverComments, b.approverComments));
          break;
        case 'status':
          sorted = base.sort((a, b) => strCmp(a.status, b.status));
          break;
        case 'projectNames':
          sorted = base.sort((a, b) => strCmp(a.projectNames, b.projectNames));
          break;
        case 'customfield':
          sorted = base.sort((a, b) =>
          {
            const va = a.customFields[column];
            const vb = b.customFields[column];
            if (!va) return -1;
            if (!vb) return 1;
            return flip * va.localeCompare(vb);
          });
          break;
        default:
          sorted = [...originalItems];
      }

      setTableSortedBy(column);
      setSortingDirection(nextDir);
      setItemsFillTable(sorted);
    },
    [tableSortedBy, sortingDirection, prevItemsFillTable, originalItems],
  );

  const onSelectRequestedIdsChange = (items: any) =>
  {
    const arr: Person[] = items ?? [];
    const ids = arr.map(i => (i.PersonID ? i.PersonID : i));
    setSelectRequestedIds(arr);
    fetchHireDate(ids.length === 1 ? (ids[0] as number) : null);
  };

  const exportToExcel = useCallback(() =>
  {
    if (!itemsFillTable.length) return;
    const customFieldsHeaders = Array.from(
      new Set(
        itemsFillTable.flatMap(item => Object.keys(item.customFields).map(getCustomFieldHeader)),
      ),
    );
    const rows = itemsFillTable.map(
      ({
        id,
        creationDate,
        team,
        requestedBy,
        employeeID,
        leadName,
        customFields,
        requestTypeName,
        days,
        dateFrom,
        dateTo,
        workedOn,
        daysLeft,
        startPeriod,
        entryDate,
        requesterComments,
        approverComments,
        status,
        projectNames,
      }) => ({
        id,
        creationDate,
        team,
        requestedBy,
        employeeID,
        leadName,
        ...customFields,
        requestTypeName,
        days,
        dateFrom,
        dateTo,
        workedOn,
        daysLeft,
        startPeriod,
        entryDate,
        requesterComments,
        approverComments,
        status,
        projectNames,
      }),
    );

    const ws = XLSX.utils.json_to_sheet(rows);
    const headers = [
      'Id',
      'Created On',
      'Team',
      'Requested By',
      'EmployeeID',
      'Lead',
      ...customFieldsHeaders,
      'Request Type',
      'Days',
      'From',
      'To',
      'Worked On',
      'Current Days Left',
      'Current Start Period',
      'Entry Date',
      'Requester Comments',
      'Approver Comments',
      'Status',
      'Project Names',
    ];
    const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
    for (let c = range.s.c; c <= range.e.c; c++)
    {
      const addr = XLSX.utils.encode_cell({ r: 0, c });
      if (ws[addr]?.t === 's') ws[addr].v = headers[c];
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, 'PTORequests.xlsx');
  }, [itemsFillTable]);

  const isDark = document.documentElement.classList.contains('dark');

  // Shared report select style, same as the other reports
  const comboBoxSelectStyles = buildReportSelectStyles<any, true>(isDark);

  const SelectAny = Select as any;
  const sharedProps = {
    autoFocus: false,
    searchable: true,
    openOnFocus: true,
    autosize: false,
    closeOnSelect: false,
    onBlur: () => {},
    styles: comboBoxSelectStyles,
    components: { DropdownIndicator: () => null, IndicatorSeparator: () => null, ClearIndicator },
    closeMenuOnSelect: false,
    isClearable: true,
    isSearchable: true,
  };

  function onDisableScroll(elementId: string)
  {
    document
      .getElementById(elementId)
      ?.querySelector<HTMLElement>('div.is-open')
      ?.classList.remove('is-open');
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <div className="PTOReport" id="PTOReport">
        {/* Header */}
        <div className="spaceRow">
          <div className="col-md-12">
            <div className="stime-header">
              <h3>PTO Report</h3>
              {showOnlyFirstVacation && (
                <p className="info_AR">Remember: First Requests are from selected period.</p>
              )}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="row adjust-filter-positions">
          {/* Requested by */}
          <div className="col-md-2 marginTopInputs">
            <label
              className="stime-label pull-left enable-help-tooltip"
              data-title="Search by Name or Id"
            >
              Requested by:
            </label>
            <div
              id="select-render-ttrc-RequestedIDBy"
              className="corgee_com_custom-fields-combobox-multiselect input-group"
            >
              <SelectAny
                {...sharedProps}
                options={peopleAvailable}
                getOptionLabel={(option: any) => option.Name}
                getOptionValue={(option: any) => String(option.PersonID)}
                onChange={onSelectRequestedIdsChange}
                isMulti={true}
                value={selectRequestedIds}
                onMenuOpen={() => onDisableScroll('select-render-ttrc-RequestedIDBy')}
              />
            </div>
          </div>

          {/* Teams */}
          <div className="col-md-2 marginTopInputs">
            <label className="stime-label pull-left">Team(s):</label>
            <div
              id="select-render-ttrc-Teams"
              className="corgee_com_custom-fields-combobox-multiselect input-group"
            >
              <SelectAny
                {...sharedProps}
                options={itemsTeams}
                getOptionLabel={(option: any) => option.Value}
                getOptionValue={(option: any) => String(option.BasicFieldValueId)}
                onChange={(items: any) => setSelectTeamsId(items ?? [])}
                isMulti={true}
                value={selectTeamsId}
                onMenuOpen={() => onDisableScroll('select-render-ttrc-Teams')}
              />
            </div>
          </div>

          {/* Departments */}
          <div className="col-md-2 marginTopInputs">
            <label className="stime-label pull-left">Department(s):</label>
            <div
              id="select-render-ttrc-Departments"
              className="corgee_com_custom-fields-combobox-multiselect input-group"
            >
              <SelectAny
                {...sharedProps}
                options={itemsDepartments}
                getOptionLabel={(option: any) => option.Value}
                getOptionValue={(option: any) => String(option.BasicFieldValueId)}
                onChange={(items: any) => setSelectDepartmentsId(items ?? [])}
                value={selectDepartmentsId}
                isMulti={true}
                onOpen={() => onDisableScroll('select-render-ttrc-Departments')}
              />
            </div>
          </div>

          {/* Leads */}
          <div className="col-md-2 marginTopInputs">
            <label className="stime-label pull-left">Lead(s):</label>
            <div
              id="select-render-ttrc-Leads"
              className="corgee_com_custom-fields-combobox-multiselect input-group"
            >
              <SelectAny
                {...sharedProps}
                options={peopleAvailable}
                getOptionLabel={(option: any) => option.Name}
                getOptionValue={(option: any) => String(option.PersonID)}
                onChange={(items: any) =>
                {
                  setSelectLeadId(items ?? []);
                }}
                value={selectLeadId}
                isMulti={true}
                onOpen={() => onDisableScroll('select-render-ttrc-Leads')}
              />
            </div>
          </div>

          {/* Request Type */}
          <div className="col-md-2 marginTopInputs d-flex layer-bottom-row layer-top-row">
            <label className="stime-label pull-left">Request Type(s):</label>
            <div
              id="select-render-ttrc-RequestType"
              className="corgee_com_custom-fields-combobox-multiselect input-group"
            >
              <SelectAny
                {...sharedProps}
                options={requestTypes}
                getOptionLabel={(option: any) => option.label}
                getOptionValue={(option: any) => option.value}
                onChange={(items: any) =>
                {
                  setSelectRequestType(items ?? []);
                }}
                value={selectRequestType}
                isMulti={true}
                onOpen={() => onDisableScroll('select-render-ttrc-RequestType')}
              />
            </div>
          </div>

          {/* Status */}
          <div className="col-md-2 marginTopInputs d-flex layer-bottom-row layer-top-row">
            <label className="stime-label pull-left">Status(es):</label>
            <div
              id="select-render-ttrc-Status"
              className="corgee_com_custom-fields-combobox-multiselect input-group"
            >
              <SelectAny
                {...sharedProps}
                options={statusList}
                getOptionLabel={(option: any) => option.label}
                getOptionValue={(option: any) => option.value}
                onChange={(items: any) =>
                {
                  setSelectStatusId(items ?? []);
                }}
                value={selectStatusId}
                isMulti={true}
                onOpen={() => onDisableScroll('select-render-ttrc-Status')}
              />
            </div>
          </div>

          {/* Projects */}
          <div className="col-md-2 marginTopInputs d-flex layer-bottom-row layer-top-row">
            <label className="stime-label pull-left">Project(s):</label>
            <div
              id="select-render-ttrc-Project"
              className="corgee_com_custom-fields-combobox-multiselect input-group"
            >
              <SelectAny
                {...sharedProps}
                options={itemsProjects}
                getOptionLabel={(option: any) => option.Name}
                getOptionValue={(option: any) => String(option.ProjectID)}
                onChange={(items: any) =>
                {
                  setSelectProjectId(items ?? []);
                }}
                value={selectProjectId}
                isMulti={true}
                labelKey="Name"
                valueKey="ProjectID"
                onOpen={() => onDisableScroll('select-render-ttrc-Project')}
              />
            </div>
          </div>

          {/* From */}
          <div className="col-md-2 marginTopInputs d-flex layer-bottom-row layer-top-row pto-filter-newrow">
            <label className="stime-label pull-left">From:</label>
            <DatePicker
              selected={selectedDateFrom ? moment(selectedDateFrom, 'YYYY-MM-DD').toDate() : null}
              onChange={(d: any) =>
              {
                setValidationFilter(false);
                setSelectedDateFrom(d ? moment(d).format('YYYY-MM-DD') : '');
                setSelectedDateFromEmpty(!d);
                setSelectedDateFromLess(false);
              }}
              dateFormat="MM/dd/yyyy"
              customInput={<PtoDateInput onClear={() => setSelectedDateFrom('')} />}
              popperProps={{ strategy: 'fixed' }}
              showPopperArrow={false}
              calendarClassName="er-datepicker-calendar"
            />
            {selectedDateFromEmpty && (
              <div className="cw-validation-Note">
                <div className="cw-validation-Message">You have not selected a Date.</div>
              </div>
            )}
            {selectedDateFromLess && (
              <div className="cw-validation-Note">
                <div className="cw-validation-Message">The from date must be less.</div>
              </div>
            )}
          </div>

          {/* To */}
          <div className="col-md-2 marginTopInputs d-flex layer-bottom-row layer-top-row">
            <label className="stime-label pull-left">To:</label>
            <DatePicker
              selected={selectedDateTo ? moment(selectedDateTo, 'YYYY-MM-DD').toDate() : null}
              onChange={(d: any) =>
              {
                setValidationFilter(false);
                setSelectedDateTo(d ? moment(d).format('YYYY-MM-DD') : '');
                setSelectedDateToEmpty(!d);
                setSelectedDateFromLess(false);
              }}
              dateFormat="MM/dd/yyyy"
              customInput={<PtoDateInput onClear={() => setSelectedDateTo('')} />}
              popperProps={{ strategy: 'fixed' }}
              showPopperArrow={false}
              calendarClassName="er-datepicker-calendar"
            />
            {selectedDateToEmpty && (
              <div className="cw-validation-Note">
                <div className="cw-validation-Message">You have not selected a Date.</div>
              </div>
            )}
          </div>

          {/* Only First Request */}
          <div className="col-md-2 marginTopInputs pto-filter-check">
            <div className="form-group" style={{ margin: 0 }}>
              <span style={{ margin: 0, verticalAlign: 'middle' }}>
                <span className="custom-checkbox task-card-checkbox" style={{ margin: -1 }}>
                  <input
                    id="CheckboxOnlyFirst"
                    className="taskCardCheckbox"
                    type="checkbox"
                    checked={showOnlyFirstVacation}
                    onChange={() => {}}
                  />
                  <label
                    htmlFor="CheckboxOnlyFirst"
                    className="conversationCheckbox"
                    onClick={() => setShowOnlyFirstVacation(p => !p)}
                  />
                </span>
                <span className="unassignedCheckbox">Only First Request</span>
              </span>
            </div>
          </div>

          {/* Apply Filters starts a new grid row, aligned under the first combo */}
          <div className="col-md-2 marginTopInputs pto-filter-apply">
            <button
              type="button"
              className="custom2 update buttonAction btnChangeColorWithLayer"
              onClick={getResults}
            >
              Apply Filters
            </button>
          </div>
        </div>

        {/* Action buttons */}
        <div className="container-button-navigation-pto">
          <button
            disabled={!itemsFillTable.length}
            className="view-bar-button enable-help-tooltip buttonExport md-button btnexportpadding"
            type="button"
            data-title="Export to Excel"
            onClick={exportToExcel}
          >
            <span className="material-icons style-icon-cloud">cloud_download</span>
            <span>&nbsp;Export</span>
          </button>
        </div>

        {/* PTO summary cards */}
        {filterApplied && hireDate && (
          <div className="dispTable">
            <div className="marginCalcs">
              <div className="stime-bottom-div enable-help-tooltip" data-title="Vacation Days Left">
                <div className="stime-bottom-title">
                  {vacationsLeft} / {vacationsPerYear}
                </div>
                <span className="stime-bottom-subtitle">Vacation Left</span>
              </div>
              {vacationsLeft === 0 && (
                <div
                  className="stime-top-div enable-help-tooltip"
                  data-title={`Your next period vacation left ${vacationsLeftNextPeriod} / ${vacationsPerYearNextPeriod}`}
                >
                  <i
                    className="fa fa-info-circle"
                    style={{ display: 'inline-block', width: 20, color: '#999' }}
                  />
                </div>
              )}
            </div>
            <div className="marginCalcs">
              <div className="stime-bottom-div enable-help-tooltip" data-title="Switch Days Left">
                <div className="stime-bottom-title">
                  {totalSwitchesDays} / {switchesAvailable}
                </div>
                <span className="stime-bottom-subtitle">Switch Work Days</span>
              </div>
            </div>
            <div className="marginCalcs">
              <div className="stime-bottom-div enable-help-tooltip" data-title="Switch Days Left">
                <div className="stime-bottom-title">
                  {totalSwitchesOff} / {switchesAvailable}
                </div>
                <span className="stime-bottom-subtitle">Switch Days Off</span>
              </div>
            </div>
            <div className="marginCalcs">
              <div className="stime-bottom-div enable-help-tooltip" data-title="Days Off Left">
                <div className="stime-bottom-title">
                  {dayOffLeft} / {dayOffAvailable}
                </div>
                <span className="stime-bottom-subtitle">Day Off Left</span>
              </div>
            </div>
            <div className="spaceCell" />
            <div className="marginCalcs">
              <div className="stime-bottom-div enable-help-tooltip" data-title="Entry Date">
                <div className="stime-bottom-title">{hireDate ?? '-'}</div>
                <span className="stime-bottom-subtitle">Entry Date</span>
              </div>
            </div>
            <div className="marginCalcs">
              <div className="stime-bottom-div enable-help-tooltip" data-title="Labor Old">
                <div className="stime-bottom-title">
                  {yearsOfService ?? '-'}
                </div>
                <span className="stime-bottom-subtitle">Labor Old</span>
              </div>
            </div>
            <div className="marginCalcs">
              <div className="stime-bottom-div enable-help-tooltip" data-title="Start Period">
                <div className="stime-bottom-title">
                  {renewalDate ? strDateToMMDDYYYY(renewalDate) : '-'}
                </div>
                <span className="stime-bottom-subtitle">Start Period</span>
              </div>
            </div>
            <div className="marginCalcs">
              <div className="stime-bottom-div enable-help-tooltip" data-title="End Period">
                <div className="stime-bottom-title">
                  {nextRenewalDate ? strDateToMMDDYYYY(nextRenewalDate) : '-'}
                </div>
                <span className="stime-bottom-subtitle">End Period</span>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="pto-table-section">
          {/* <div className="text-center col-md-2" style={{ position: "absolute", marginLeft: 0, width: "85%", marginTop: 100, zIndex: 2, visibility: isLoadingRequests ? "visible" : "hidden" }}>
                <div className="spinner-border" role="status"><span className="sr-only">Loading...</span></div>
            </div> */}
          <div
            className="text-center"
            style={{ position: 'absolute', marginLeft: 0, width: '85%', marginTop: 160 }}
          >
            <div
              className="spinner-border"
              role="status"
              style={{ display: isLoadingRequests ? 'inline-flex' : 'none' }}
            >
              <span className="sr-only">Loading...</span>
            </div>
          </div>

          <div id="tableContainerPTO" className="table-responsive wrapper table-wrapper">
            <table className="noTableBottom table table-sm table-bordered">
              <thead>
                <tr>
                  <SortTh
                    col="creationDate"
                    label="Created On"
                    sorted={tableSortedBy}
                    dir={sortingDirection}
                    onSort={sortTableBy}
                  />
                  <SortTh
                    col="team"
                    label="Team"
                    sorted={tableSortedBy}
                    dir={sortingDirection}
                    onSort={sortTableBy}
                  />
                  <SortTh
                    col="requestedBy"
                    label="Requested By"
                    sorted={tableSortedBy}
                    dir={sortingDirection}
                    onSort={sortTableBy}
                  />
                  <SortTh
                    col="employeeID"
                    label="EmployeeID"
                    sorted={tableSortedBy}
                    dir={sortingDirection}
                    onSort={sortTableBy}
                  />
                  <SortTh
                    col="lead"
                    label="Lead"
                    sorted={tableSortedBy}
                    dir={sortingDirection}
                    onSort={sortTableBy}
                    tooltip="leadName"
                  />
                  {Object.keys(dataTableTotalCustomField).map(key => (
                    <th key={key} onClick={() => sortTableBy(key)}>
                      <span className="headerIconPadding">{getCustomFieldHeader(key)}</span>
                      <SortIcon col={key} sorted={tableSortedBy} dir={sortingDirection} />
                    </th>
                  ))}
                  <SortTh
                    col="requestType"
                    label="Request Type"
                    sorted={tableSortedBy}
                    dir={sortingDirection}
                    onSort={sortTableBy}
                  />
                  <SortTh
                    col="days"
                    label="Days"
                    sorted={tableSortedBy}
                    dir={sortingDirection}
                    onSort={sortTableBy}
                  />
                  <SortTh
                    col="from"
                    label="From"
                    sorted={tableSortedBy}
                    dir={sortingDirection}
                    onSort={sortTableBy}
                    tooltip="Date From"
                  />
                  <SortTh
                    col="to"
                    label="To"
                    sorted={tableSortedBy}
                    dir={sortingDirection}
                    onSort={sortTableBy}
                    tooltip="Date To"
                  />
                  <SortTh
                    col="workedOn"
                    label="Worked On"
                    sorted={tableSortedBy}
                    dir={sortingDirection}
                    onSort={sortTableBy}
                  />
                  <SortTh
                    col="daysLeft"
                    label="Current Days Left"
                    sorted={tableSortedBy}
                    dir={sortingDirection}
                    onSort={sortTableBy}
                    tooltip="Vacation Days Left"
                  />
                  <SortTh
                    col="startPeriod"
                    label="Current Start Period"
                    sorted={tableSortedBy}
                    dir={sortingDirection}
                    onSort={sortTableBy}
                    tooltip="Start Period"
                  />
                  <SortTh
                    col="entryDate"
                    label="Entry Date"
                    sorted={tableSortedBy}
                    dir={sortingDirection}
                    onSort={sortTableBy}
                  />
                  <SortTh
                    col="requesterComments"
                    label="Requester Comments"
                    sorted={tableSortedBy}
                    dir={sortingDirection}
                    onSort={sortTableBy}
                  />
                  <SortTh
                    col="approverComments"
                    label="Approver Comments"
                    sorted={tableSortedBy}
                    dir={sortingDirection}
                    onSort={sortTableBy}
                  />
                  <SortTh
                    col="status"
                    label="Status"
                    sorted={tableSortedBy}
                    dir={sortingDirection}
                    onSort={sortTableBy}
                  />
                  <SortTh
                    col="projectNames"
                    label="Project Names"
                    sorted={tableSortedBy}
                    dir={sortingDirection}
                    onSort={sortTableBy}
                    tooltip="projectNames"
                  />
                </tr>
              </thead>
              <tbody>
                {!isLoadingRequests &&
                  (!validationFilter && itemsFillTable.length > 0 ? (
                    itemsFillTable.map(item => (
                      <tr key={item.id}>
                        <td className="stime-td-report">
                          <span className={item.isFirstRendered ? 'updated' : ''} />
                          {item.creationDate}
                        </td>
                        <td className="stime-td-report">{item.team}</td>
                        <td className="stime-td-report">{item.requestedBy}</td>
                        <td className="stime-td-report">{item.employeeID}</td>
                        <td className="stime-td-report">{item.leadName}</td>
                        {Object.entries(item.customFields).map(([k, v]) => (
                          <td key={k} className="stime-td-report">
                            {v}
                          </td>
                        ))}
                        <td className="stime-td-report">{item.requestTypeName}</td>
                        <td className="stime-td-report">{item.days}</td>
                        <td className="stime-td-report">{item.dateFrom}</td>
                        <td className="stime-td-report">{item.dateTo}</td>
                        <td className="stime-td-report">{item.workedOn}</td>
                        <td
                          className="stime-td-report enable-help-tooltip"
                          data-title={
                            item.daysLeft === '-'
                              ? 'Request out of period'
                              : `${item.requestTypeName} days left`
                          }
                        >
                          {item.daysLeft}
                        </td>
                        <td className="stime-td-report">{item.startPeriod}</td>
                        <td className="stime-td-report">{item.entryDate}</td>
                        <td className="stime-td-report comments">{item.requesterComments}</td>
                        <td className="stime-td-report comments">{item.approverComments}</td>
                        <td className="stime-td-report">{item.status}</td>
                        <td
                          className="stime-td-report text enable-help-tooltip"
                          data-title={item.projectNames}
                        >
                          {item.projectNames}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={14} style={{ border: 'none' }}>
                        <div className="row spaceRow" />
                        <div className="row spaceRow" />
                        <div className="row spaceRow">
                          <div className="col-md-12">
                            {!validationFilter && (
                              <div className="stime-bottom-Results notFoundRecords">
                                No results found
                              </div>
                            )}
                            {validationFilter && (
                              <div className="stime-bottom-Results notFoundRecords">
                                Please enter at least one filter
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="row spaceRow" />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showDateRangeMsg &&
        createPortal(
          <>
            <style>{`
            @keyframes pto-date-msg {
              0%   { opacity: 0; }
              10%  { opacity: 1; }
              90%  { opacity: 1; }
              100% { opacity: 0; }
            }
          `}</style>
            <div
              style={{
                position: 'fixed',
                top: '64px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: '#62abcb',
                color: '#fff',
                padding: '8px 20px',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 500,
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                pointerEvents: 'none',
                zIndex: 9999,
                whiteSpace: 'nowrap',
                animation: 'pto-date-msg 3s ease forwards',
              }}
            >
              The start date must be within the last 2 years
            </div>
          </>,
          document.body,
        )}
    </>
  );
};

export default PTOReport;
